// @ts-strict-ignore
import React, { useContext } from 'react';
import { createWorkflowHistoryThunk, deleteRuleFromStateThunk } from 'flow/store/redux/thunks';
import { useAppDispatch } from '~/store/reduxHooks';
import { RuleBuilderContext } from '../../context';
import type { RuleBuilderRule } from 'flow/service/rules';
import { findRule, getLocalizedIndex, getPathToParent } from 'flow/service/rules';
import type { GroupPosition } from '../../types';
import { parenthesisOptions } from './constants';
import { setRuleBuilderPendingGroupInfo, createWorkflowOperationMessage } from 'flow/store/redux/slice';
import { v4 as uuidv4 } from 'uuid';
import { OperationMessageType } from 'flow/store/engine/types';
import { RuleUpdateOperationType } from 'flow/store/engine/update/constants';
import RuleMultiSelect from '../RuleTemplate/RuleMultiSelect';
import type { WorkflowStateChange } from 'flow/store/redux/types';
import { changeDescription } from 'flow/store/redux/types';
import { Operation } from 'flow/service/changelog/types';
import { UserContext } from '~/dashboard/context';
import { createUiEffectContext } from 'flow/service/history';

interface ParenthesisSelectProps {
  rule: RuleBuilderRule;
  groupPosition: GroupPosition;
}

export default function ParenthesisSelect({ rule, groupPosition }: ParenthesisSelectProps) {
  const dispatch = useAppDispatch();
  const { sourceAction, ruleTree, isTreeAmbiguous } = useContext(RuleBuilderContext);
  const { featureFlags } = useContext(UserContext);

  const onOpenGroup = () => {
    const parentRulePath = getPathToParent(rule.data.path);
    const parentRule = findRule(ruleTree, { rulePath: parentRulePath });

    dispatch(
      setRuleBuilderPendingGroupInfo({
        parentRuleId: parentRule.id,
        startIndex: getLocalizedIndex(rule.data.path),
      }),
    );

    // Show assistance message for pending group
    dispatch(createWorkflowOperationMessage({
      object: {
        id: uuidv4(),
        publicId: sourceAction.publicId,
        type: OperationMessageType.actionRuleThreshold,
        timestamp: Date.now(),
        message: RuleUpdateOperationType.CANCEL_PENDING_RULE_GROUP,
        data: undefined,
      },
    }));
  };

  function handleDispatch(stateChange: WorkflowStateChange) {
    dispatch(
      createWorkflowHistoryThunk(
        stateChange,
        Boolean(featureFlags?.workflowEditorUndoRedo),
        {
          operation: Operation.Remove,
          uiEffectContext: createUiEffectContext(changeDescription.group, {
            actionId: sourceAction.publicId,
          }),
        },
      ),
    );
  }

  const onChange = option => {
    if (option.value === 'ungroup') {
      handleDispatch(
        () => dispatch(deleteRuleFromStateThunk({
          action: sourceAction,
          treeRoot: ruleTree,
          rule,
        })),
      );
    } else if (option.value === 'open') {
      onOpenGroup();
    }
  };

  return (
    <RuleMultiSelect
      options={parenthesisOptions}
      onChange={onChange}
      placeholder={groupPosition === 'start' ? '(' : ')'}
      selectedOptions={null}
      isDisabled={isTreeAmbiguous}
      isMulti={false}
      isNaked
    />
  );
}
